'use client';

import Image from 'next/image';
import React, { useState } from 'react';
import PlayMiniFillIcon from 'remixicon-react/PlayMiniFillIcon';
import CloseLineIcon from 'remixicon-react/CloseLineIcon';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { LayoutProps } from '@/types/generalSettings';

interface TopFooterProps {
    topFooter: LayoutProps.TopFooterData | null;
    className?: string;
}

/** «About us» text with the founder's intro video (opens in a dialog). */
const TopFooter: React.FC<TopFooterProps> = ({ topFooter, className = '' }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { topFooterText, topFooterTextBottom, footerVideo } = topFooter || {};
    const cover = footerVideo?.videoCover?.sourceUrl;
    const video = footerVideo?.videoFile?.mediaItemUrl;

    if (!topFooterText && !topFooterTextBottom) return null;

    return (
        // Phones: a small «intro video» row above a full-width text; desktop: thumbnail beside the text
        <div className={`flex flex-col gap-4 lg:flex-row lg:items-start ${className}`}>
            {cover && video && (
                <button
                    type='button'
                    onClick={() => setIsModalOpen(true)}
                    aria-label='پخش ویدیوی معرفی خانه سرمایه'
                    className='group flex w-fit items-center gap-3 rounded-2xl text-start max-lg:border max-lg:border-zinc-200 max-lg:bg-white max-lg:p-2 max-lg:pe-4 dark:max-lg:border-white/10 dark:max-lg:bg-white/[0.04] lg:mt-1 lg:shrink-0'
                >
                    <span className='relative shrink-0'>
                        <Image
                            src={cover}
                            width={64}
                            height={64}
                            alt={footerVideo?.videoCover?.title || 'معرفی خانه سرمایه'}
                            className='size-11 rounded-xl object-cover ring-1 ring-black/5 dark:ring-white/10 lg:size-16 lg:rounded-2xl'
                        />
                        <span className='absolute -bottom-1 -start-1 flex size-5 items-center justify-center rounded-full bg-secondary text-white ring-2 ring-white transition-transform duration-300 group-hover:scale-110 dark:ring-primary lg:size-6 lg:ring-gray-100'>
                            <PlayMiniFillIcon size={14} />
                        </span>
                    </span>
                    <span className='flex flex-col gap-0.5 lg:hidden'>
                        <span className='text-sm font-bold text-zinc-900 dark:text-white'>
                            معرفی خانه سرمایه
                        </span>
                        <span className='text-xs text-progress dark:text-[#9FB0FF]'>
                            تماشای ویدیو
                        </span>
                    </span>
                </button>
            )}
            <p className='text-[13px] leading-7 text-zinc-600 dark:text-white/65 max-lg:text-justify'>
                {topFooterText} {topFooterTextBottom}
            </p>

            {video && (
                <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
                    <DialogContent
                        dir='rtl'
                        overlayClassName='bg-black/75 backdrop-blur-sm'
                        // [&>button:last-child]: the dialog's built-in close (its own header has one)
                        className='w-[calc(100%-1.5rem)] max-w-3xl gap-0 overflow-hidden rounded-2xl border-white/10 bg-zinc-950 p-0 shadow-2xl sm:rounded-2xl [&>button:last-child]:hidden'
                    >
                        <div className='flex items-center justify-between gap-3 px-4 py-3 lg:px-5'>
                            <DialogTitle className='text-sm font-bold text-white lg:text-base'>
                                معرفی خانه سرمایه
                            </DialogTitle>
                            <button
                                type='button'
                                aria-label='بستن'
                                onClick={() => setIsModalOpen(false)}
                                className='flex size-9 shrink-0 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60'
                            >
                                <CloseLineIcon size={20} />
                            </button>
                        </div>
                        {/* Fixed 16:9 frame with the cover as poster: no empty white box or jump
                            while the video loads */}
                        <div className='relative aspect-video w-full bg-black'>
                            {isModalOpen && (
                                <video
                                    controls
                                    autoPlay
                                    playsInline
                                    preload='metadata'
                                    poster={cover}
                                    className='absolute inset-0 size-full object-contain'
                                    src={video}
                                />
                            )}
                        </div>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
};

export default TopFooter;
